import React, { useState } from 'react';
import { Box, Button, Chip, Menu, MenuItem, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * Props:
 *   filters:       [{ column, value }]         active filters
 *   filterOptions: { [column]: [values] }       available values per column
 *   columnLabels:  { [column]: string }         display names for columns
 *   onAdd:         (column, value) => void
 *   onRemove:      (column) => void
 *   onClear:       () => void
 */
const FilterBar = ({ filters, filterOptions, columnLabels, onAdd, onRemove, onClear }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [step, setStep] = useState('column');   // 'column' | 'value'
  const [activeCol, setActiveCol] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = (e) => { setAnchorEl(e.currentTarget); setStep('column'); setActiveCol(null); };
  const handleClose = () => { setAnchorEl(null); setStep('column'); setActiveCol(null); };

  const pickColumn = (col) => { setActiveCol(col); setStep('value'); };
  const pickValue  = (val) => { onAdd(activeCol, val); handleClose(); };

  // Columns not yet filtered
  const available = Object.keys(filterOptions).filter(col => !filters.find(f => f.column === col));

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      px: 1.5, py: 1, bgcolor: '#fafafa', borderBottom: '1px solid #f1f5f9', minHeight: 44,
    }}>

      {/* Active chips */}
      {filters.map(f => (
        <Chip
          key={f.column}
          label={`${columnLabels[f.column] || f.column}: ${f.value}`}
          onDelete={() => onRemove(f.column)}
          size="small"
          sx={{
            bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
            fontWeight: 500, fontSize: '0.75rem', height: 26,
            '& .MuiChip-deleteIcon': { color: '#93c5fd', '&:hover': { color: '#1d4ed8' } },
          }}
        />
      ))}

      {/* Add filter */}
      {available.length > 0 && (
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 13 }} />}
          onClick={handleOpen}
          sx={{
            fontSize: '0.75rem', fontWeight: 500, color: '#64748b', height: 26,
            border: '1px dashed #cbd5e1', borderRadius: 1.5, px: 1.25, py: 0,
            minWidth: 0, lineHeight: 1,
            '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8', color: '#0f172a', borderStyle: 'solid' },
          }}
        >
          Add filter
        </Button>
      )}

      {/* Clear all */}
      {filters.length > 0 && (
        <Button
          size="small"
          onClick={onClear}
          sx={{
            fontSize: '0.75rem', color: '#94a3b8', height: 26, minWidth: 0, px: 1,
            '&:hover': { color: '#dc2626', bgcolor: 'transparent' },
          }}
        >
          Clear all
        </Button>
      )}

      {/* Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 200, borderRadius: 1.5,
            border: '1px solid #e2e8f0',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            mt: 0.5,
          }
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {step === 'column' && [
          <Box key="hdr" sx={{ px: 2, py: 1, borderBottom: '1px solid #f1f5f9' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Filter by
            </Typography>
          </Box>,
          ...available.map(col => (
            <MenuItem
              key={col}
              onClick={() => pickColumn(col)}
              sx={{ fontSize: '0.8125rem', py: 1, '&:hover': { bgcolor: '#f8fafc' } }}
            >
              {columnLabels[col] || col}
            </MenuItem>
          )),
        ]}

        {step === 'value' && [
          <Box key="hdr" sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setStep('column')} sx={{ p: 0.25 }}>
              <ArrowBackIcon sx={{ fontSize: 14, color: '#64748b' }} />
            </IconButton>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {columnLabels[activeCol] || activeCol}
            </Typography>
          </Box>,
          ...(filterOptions[activeCol] || []).map(val => (
            <MenuItem
              key={val}
              onClick={() => pickValue(val)}
              sx={{ fontSize: '0.8125rem', py: 1, '&:hover': { bgcolor: '#f8fafc' } }}
            >
              {val}
            </MenuItem>
          )),
        ]}
      </Menu>
    </Box>
  );
};

export default FilterBar;
